import { z } from "zod";

/**
 * Every resource carries its own `abilities` map. This is the server-driven policy contract:
 * the UI reads permissions, it never derives them. An absent key means "not allowed".
 */
export const AbilitiesSchema = z.record(z.string(), z.boolean());
export type Abilities = z.infer<typeof AbilitiesSchema>;

export const ActorSchema = z.object({
  id: z.string(),
  name: z.string(),
  avatarUrl: z.string().nullable(),
});
export type Actor = z.infer<typeof ActorSchema>;

export const ViewerSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  avatarUrl: z.string().nullable(),
  language: z.string(),
  role: z.string(),
});
export type Viewer = z.infer<typeof ViewerSchema>;

export const WorkspaceSchema = z.object({
  id: z.string(),
  name: z.string(),
  subdomain: z.string().nullable(),
  avatarUrl: z.string().nullable().default(null),
  /** The workspace this session is scoped to. */
  current: z.boolean().default(false),
  suspended: z.boolean().default(false),
  /** Opens automatically, skipping the picker. */
  autoEnter: z.boolean().default(false),
  lastSelectedAt: z.string().nullable().default(null),
  /**
   * W4 §E-5 — the workspace's preference document, whole and untyped.
   *
   * `team.update` REPLACES it wholesale server-side, so a caller that posts a bare patch
   * erases every key it did not know about. Parsing the current object here is what makes a
   * client-side merge possible at all: `workspaces.updatePreferences` reads this, spreads the
   * patch over it, and posts the result — the same read-modify-write `health.saveSettings`
   * already does, through the same code path.
   */
  preferences: z.record(z.string(), z.unknown()).default({}),
  abilities: AbilitiesSchema,
});
export type Workspace = z.infer<typeof WorkspaceSchema>;

export const CollectionSchema = z.object({
  id: z.string(),
  name: z.string(),
  emoji: z.string().nullable(),
  description: z.string(),
  index: z.number(),
  // W5: the project a collection has been grouped under, if any (`PresentCollection`'s
  // `projectId`). Nullable — most collections belong to no project — and defaulted so an
  // adapter or fixture that predates this field still parses.
  projectId: z.string().nullable().default(null),
  /**
   * W10 §8 — when the collection was archived, or null. Archiving hides every document in it,
   * which is why `collections.list` excludes archived collections unless `statusFilter`
   * contains `"archived"`; there is no `collections.archived` endpoint. Defaulted so an adapter
   * or fixture that predates the field still parses.
   */
  archivedAt: z.string().nullable().default(null),
  abilities: AbilitiesSchema,
});
export type Collection = z.infer<typeof CollectionSchema>;

/**
 * One document type covers every list in the app. Drafts, archive and trash are *states*
 * (`publishedAt` / `archivedAt` / `deletedAt`), not separate resources — which is why
 * /drafts, /archive and /trash are the same query with a different filter.
 */
export const DocumentSchema = z.object({
  id: z.string(),
  title: z.string(),
  emoji: z.string().nullable(),
  summary: z.string(),
  /**
   * The document body as markdown source — what the platform's `text` field carries.
   * Defaulted so adapters whose list endpoints omit bodies still parse; the reader scene
   * renders it, so documents.info must supply it.
   */
  text: z.string().default(""),
  collectionId: z.string().nullable(),
  parentDocumentId: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  publishedAt: z.string().nullable(),
  archivedAt: z.string().nullable(),
  deletedAt: z.string().nullable(),
  createdBy: ActorSchema,
  views: z.number(),
  /**
   * W6 — which editing lane opens this document. Defaulted, because it rides `documents.info`
   * on the platform and nowhere else; every other adapter's documents are `"v1"`.
   *
   * ⚠ On a `"v2"` document `text` above is the v1 baseline FROZEN at opt-in:
   * `ApiPresenters.cs` projects `text` from the v1 column and never reads `textV2`. So a
   * reader that renders `text` for a v2 document is showing the past — the live content lives
   * in the `document-v2.<id>` CRDT room and is read through `editorV2`.
   */
  editorLane: z.enum(["v1", "v2"]).default("v1"),
  abilities: AbilitiesSchema,
});
export type Document = z.infer<typeof DocumentSchema>;

export type DocumentStatus = "published" | "draft" | "archived" | "deleted";

export function documentStatus(document: Document): DocumentStatus {
  if (document.deletedAt) {
    return "deleted";
  }
  if (document.archivedAt) {
    return "archived";
  }
  return document.publishedAt ? "published" : "draft";
}

export const RevisionSchema = z.object({
  id: z.string(),
  documentId: z.string(),
  title: z.string(),
  summary: z.string(),
  createdAt: z.string(),
  createdBy: ActorSchema,
});
export type Revision = z.infer<typeof RevisionSchema>;

export const MemberSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  avatarUrl: z.string().nullable(),
  role: z.string(),
  suspended: z.boolean(),
  lastActiveAt: z.string().nullable(),
  abilities: AbilitiesSchema,
});
export type Member = z.infer<typeof MemberSchema>;

export const GroupSchema = z.object({
  id: z.string(),
  name: z.string(),
  memberCount: z.number(),
  abilities: AbilitiesSchema,
});
export type Group = z.infer<typeof GroupSchema>;

export const ApiKeySchema = z.object({
  id: z.string(),
  name: z.string(),
  partialSecret: z.string(),
  createdAt: z.string(),
  lastActiveAt: z.string().nullable(),
  abilities: AbilitiesSchema,
});
export type ApiKey = z.infer<typeof ApiKeySchema>;

export const ShareSchema = z.object({
  id: z.string(),
  documentId: z.string(),
  documentTitle: z.string(),
  url: z.string(),
  includeChildren: z.boolean(),
  views: z.number(),
  createdAt: z.string(),
  createdBy: ActorSchema,
  abilities: AbilitiesSchema,
});
export type Share = z.infer<typeof ShareSchema>;

/** What a share link resolves to: the grant and the document it grants access to. */
export const SharedDocumentSchema = z.object({
  share: ShareSchema,
  document: DocumentSchema,
});
export type SharedDocument = z.infer<typeof SharedDocumentSchema>;

export const PublicProfileSchema = z.object({
  slug: z.string(),
  name: z.string(),
  avatarUrl: z.string().nullable(),
  bio: z.string(),
  documents: z.array(DocumentSchema),
});
export type PublicProfile = z.infer<typeof PublicProfileSchema>;

/** One skill on a public CV, already ordered by level when the server sends it. */
export const PublicProfileSkillSchema = z.object({
  id: z.string(),
  name: z.string(),
  level: z.string(),
  endorsementCount: z.number(),
  projectsUsedCount: z.number(),
});
export type PublicProfileSkill = z.infer<typeof PublicProfileSkillSchema>;

/**
 * One dimension of an interview result. A score NEVER travels alone: `band` is what a reader
 * should take from it ("strong"), `note` is the evidence the interviewer cited for it. Rendering
 * the number without its note would turn a judgement into a measurement.
 */
export const CvInterviewScoreSchema = z.object({
  score: z.number(),
  band: z.string(),
  note: z.string().nullable().default(null),
});
export type CvInterviewScore = z.infer<typeof CvInterviewScoreSchema>;

/**
 * The published result as the CV carries it (`ProfilesController.PresentPublishedInterview`).
 * `at` is when the interview was TAKEN — a two-year-old assessment must read as two years old —
 * and `minutes`/`plan`/`integrity` are the rest of its provenance: how long it took, which exam
 * it was, and how the answers arrived.
 */
export const CvInterviewResultSchema = z.object({
  interviewId: z.string().nullable().default(null),
  at: z.string(),
  profession: z.string().nullable().default(null),
  years: z.number().nullable().default(null),
  hr: CvInterviewScoreSchema.nullable().default(null),
  tech: CvInterviewScoreSchema.nullable().default(null),
  cognitive: CvInterviewScoreSchema.nullable().default(null),
  overall: CvInterviewScoreSchema.nullable().default(null),
  summary: z.string().nullable().default(null),
  strengths: z.array(z.string()).default([]),
  /** What the owner chose to publish: availability, and their rate only if they ticked it. */
  facts: z.record(z.string(), z.string()).default({}),
  /** The model that conducted it — the badge names its judge. */
  model: z.string().nullable().default(null),
  /** `cv-interview-v1@1` — a score is only readable against the exam that produced it. */
  plan: z.string().nullable().default(null),
  /** How long the sitting took. Nine minutes and fifty minutes are not the same claim. */
  minutes: z.number().nullable().default(null),
  /** `verified` | `unverified` | `flagged` — how the answers ARRIVED, never a verdict on a person. */
  integrity: z.string().nullable().default(null),
  /** False when the conversation took no scores: it says the CV was built, not that it was graded. */
  scored: z.boolean().default(false),
});
export type CvInterviewResult = z.infer<typeof CvInterviewResultSchema>;

/**
 * How an answer arrived, measured by the page while it was being written. Nothing here looks at
 * the TEXT: there is no reliable way to tell generated prose from written prose by reading it,
 * and a badge built on a detector would be an accusation machine. What is measured is the act of
 * answering — typed here, at a human pace, in a window that kept focus.
 */
export const AnswerTelemetrySchema = z.object({
  elapsedMs: z.number(),
  typingMs: z.number(),
  keystrokes: z.number(),
  backspaces: z.number(),
  pasteCount: z.number(),
  pastedChars: z.number(),
  blurCount: z.number(),
  longestBurstChars: z.number(),
});
export type AnswerTelemetry = z.infer<typeof AnswerTelemetrySchema>;

/** One answered question in the transcript. */
export const CvInterviewTurnSchema = z.object({
  sectionId: z.string().default(""),
  sectionName: z.string().default(""),
  /** `opening` | `drill` | `probe`. */
  kind: z.string().default("opening"),
  question: z.string(),
  answer: z.string(),
  at: z.string().nullable().default(null),
  /** `typed` | `mixed` | `pasted` | `unverified`. */
  arrival: z.string().default("unverified"),
  /** True when the person corrected this answer after giving it. */
  edited: z.boolean().default(false),
});
export type CvInterviewTurn = z.infer<typeof CvInterviewTurnSchema>;

/** A section, folded: what it scored and why, written the moment it closed. */
export const CvInterviewSectionResultSchema = z.object({
  id: z.string(),
  name: z.string(),
  scores: z.record(z.string(), z.number()).default({}),
  note: z.string().nullable().default(null),
  specificity: z.number().default(0),
  turnCount: z.number().default(0),
  closedAt: z.string().nullable().default(null),
});
export type CvInterviewSectionResult = z.infer<typeof CvInterviewSectionResultSchema>;

/** Something the interview noticed about how it was answered, in the words the person reads. */
export const CvInterviewFindingSchema = z.object({
  code: z.string(),
  detail: z.string(),
  sectionId: z.string().default(""),
  at: z.string().nullable().default(null),
});
export type CvInterviewFinding = z.infer<typeof CvInterviewFindingSchema>;

/** A skill the interview heard evidence for — offered to the CV, never added behind the owner's back. */
export const CvInterviewSkillSchema = z.object({
  name: z.string(),
  level: z.string(),
  sectionId: z.string().nullable().default(null),
});
export type CvInterviewSkill = z.infer<typeof CvInterviewSkillSchema>;

/** One entry in the CV being written — a role, a client, or a piece of work. */
export const CvDraftEntrySchema = z.object({
  title: z.string().nullable().default(null),
  role: z.string().nullable().default(null),
  /** "2021–2024", "last year" — whatever they actually said. */
  period: z.string().nullable().default(null),
  description: z.string().nullable().default(null),
  outcome: z.string().nullable().default(null),
  technologies: z.array(z.string()).default([]),
  /** Which part of the conversation wrote this — clicking it lights those answers up. */
  sectionId: z.string().nullable().default(null),
  sectionName: z.string().nullable().default(null),
});
export type CvDraftEntry = z.infer<typeof CvDraftEntrySchema>;

/**
 * The CV as the conversation has it SO FAR — rebuilt after every section and shown to the person
 * while they talk. The point of a CV-building interview is that the thing being written is
 * visible as it is written; nobody should answer eleven questions into a void.
 */
export const CvDraftSchema = z.object({
  headline: z.string().nullable().default(null),
  summary: z.string().nullable().default(null),
  about: z.string().nullable().default(null),
  /** Provenance: the section each line came from. */
  headlineFrom: z.string().nullable().default(null),
  summaryFrom: z.string().nullable().default(null),
  aboutFrom: z.string().nullable().default(null),
  entries: z.array(CvDraftEntrySchema).default([]),
  skills: z.array(CvInterviewSkillSchema).default([]),
  languages: z.array(z.string()).default([]),
  strengths: z.array(z.string()).default([]),
  /** Nothing heard yet — the panel says so rather than showing an empty CV. */
  empty: z.boolean().default(true),
  /** True when a line is the person's own words rather than the model's. */
  pinned: z.boolean().default(false),
});
export type CvDraft = z.infer<typeof CvDraftSchema>;

/** A CV with nothing in it yet — the default, and what a fixture starts from. */
export const EMPTY_CV_DRAFT: CvDraft = {
  pinned: false,
  headline: null,
  summary: null,
  about: null,
  headlineFrom: null,
  summaryFrom: null,
  aboutFrom: null,
  entries: [],
  skills: [],
  languages: [],
  strengths: [],
  empty: true,
};

/**
 * The interview's SHAPE: a flow (`.tomato/flows/cv-interview-v1.json`), whose sections the
 * screen can show BEFORE the person is in them. `origin` says whose exam it is — `canonical`
 * (the estate's own) or `workspace` (a team authored its own, and the system badge is withheld).
 */
export const CvInterviewPlanSchema = z.object({
  slug: z.string(),
  name: z.string(),
  version: z.number().default(1),
  origin: z.string().default("canonical"),
  targetMinutes: z.number().default(60),
  maxTurns: z.number().default(34),
  sections: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
        dimensions: z.array(z.string()).default([]),
        maxTurns: z.number().default(3),
        probe: z.string().nullable().default(null),
      }),
    )
    .default([]),
});
export type CvInterviewPlan = z.infer<typeof CvInterviewPlanSchema>;

/**
 * A whole interview sitting — the thing `/market/interview` drives. The SERVER owns the script:
 * `question` is what to show, the section counters are how far along it is, and `status` flips to
 * "completed" only when the reading landed. The client never decides what comes next, which is
 * what makes this a controlled experience rather than a chat that could wander.
 */
export const CvInterviewSchema = z.object({
  id: z.string(),
  status: z.string(),
  plan: z
    .object({
      slug: z.string().default(""),
      name: z.string().default(""),
      version: z.number().default(1),
      origin: z.string().default("canonical"),
    })
    .default({ slug: "", name: "", version: 1, origin: "canonical" }),
  sectionId: z.string().default(""),
  sectionName: z.string().default(""),
  sectionNumber: z.number().default(1),
  sectionCount: z.number().default(1),
  sectionTurnCount: z.number().default(0),
  turnCount: z.number().default(0),
  maxTurns: z.number().default(34),
  targetMinutes: z.number().default(60),
  elapsedMinutes: z.number().default(0),
  question: z.string().default(""),
  questionKind: z.string().default("opening"),
  /** `timed` | `callback` when this question carries a probe. */
  probe: z.string().nullable().default(null),
  probeSeconds: z.number().nullable().default(null),
  askedAt: z.string().nullable().default(null),
  maxAnswerLength: z.number().default(4000),
  turns: z.array(CvInterviewTurnSchema).default([]),
  sections: z.array(CvInterviewSectionResultSchema).default([]),
  facts: z.record(z.string(), z.string()).default({}),
  /** The CV taking shape. What the side panel renders. */
  draft: CvDraftSchema.default(() => EMPTY_CV_DRAFT),
  /** False on a plan that takes no scores — the CV builder claims nothing about level. */
  scored: z.boolean().default(false),
  profession: z.string().nullable().default(null),
  years: z.number().nullable().default(null),
  scores: z
    .object({
      hr: CvInterviewScoreSchema.nullable().default(null),
      tech: CvInterviewScoreSchema.nullable().default(null),
      cognitive: CvInterviewScoreSchema.nullable().default(null),
      overall: CvInterviewScoreSchema.nullable().default(null),
    })
    .nullable()
    .default(null),
  summary: z.string().nullable().default(null),
  strengths: z.array(z.string()).default([]),
  skills: z.array(CvInterviewSkillSchema).default([]),
  integrity: z
    .object({
      band: z.string().default("unverified"),
      findings: z.array(CvInterviewFindingSchema).default([]),
      arrivals: z.record(z.string(), z.number()).default({}),
    })
    .default({ band: "unverified", findings: [], arrivals: {} }),
  model: z.string().nullable().default(null),
  startedAt: z.string().nullable().default(null),
  pausedAt: z.string().nullable().default(null),
  completedAt: z.string().nullable().default(null),
  publishedAt: z.string().nullable().default(null),
});
export type CvInterview = z.infer<typeof CvInterviewSchema>;

/** What `profiles.interview.get` answers: whether it is offered here, and where the caller stands. */
export const CvInterviewStateSchema = z.object({
  enabled: z.boolean(),
  model: z.string().nullable().default(null),
  plan: CvInterviewPlanSchema,
  interview: CvInterviewSchema.nullable().default(null),
});
export type CvInterviewState = z.infer<typeof CvInterviewStateSchema>;

/**
 * What `profiles.getPublic` answers (ProfilesController.cs:326-353) — a CV, not the legacy
 * published-documents page `PublicProfileSchema` describes. Two things are true by construction
 * on that anonymous route: `visibility` is null and `isOwner` is false, so neither may drive a
 * badge. `portfolio` is untyped json whose rows are PascalCase — read it with `readPortfolio`.
 * `availability` and `collaborationStyle` arrive as .NET enum names ("ProjectBased"), so every
 * comparison goes through `normalizeWire()`.
 */
export const PublicProfileCvSchema = z.object({
  id: z.string(),
  slug: z.string(),
  displayName: z.string(),
  headline: z.string().nullable(),
  bioShort: z.string().nullable(),
  bioExtended: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  timezone: z.string().nullable(),
  availability: z.string().nullable(),
  collaborationStyle: z.string().nullable(),
  languages: z.array(z.string()),
  visibility: z.string().nullable(),
  isOwner: z.boolean(),
  skills: z.array(PublicProfileSkillSchema),
  portfolio: z.unknown().nullable(),
  updatedAt: z.string().nullable(),
  /**
   * W9 (§A.4 P4) — two fields the server already presents on `Profile` that no client read
   * until now: `userId` is the cross-link to `/people/$userId`; `certified` mirrors
   * `Profile.IsCertified(now)`, presented by `marketplace.public.profiles` but — as of this
   * wave — NOT by `profiles.getPublic` (filed as backend follow-up B9). Both default so a
   * fixture or a live response that omits them still parses; `certified` reads as `false`
   * until the server actually sends it, which is honest rather than invented.
   */
  userId: z.string().nullable().default(null),
  certified: z.boolean().default(false),
  /**
   * The system interview, once its owner published it (`profiles.interview.publish`) — the
   * CV's own copy of the result, NOT the transcript. Null on a CV whose owner has not sat one
   * or took the result back off, and defaulted so a fixture or an older server still parses.
   */
  interview: CvInterviewResultSchema.nullable().default(null),
});
export type PublicProfileCv = z.infer<typeof PublicProfileCvSchema>;

/**
 * The authenticated `profiles.*` family answers the same `Profile` shape `profiles.getPublic`
 * does (§C.3: "a superset of `PublicProfileCvSchema` — reuse, do not fork"). Rather than declare
 * a second schema that could drift from this one, `ProfileCv` IS `PublicProfileCv` — the only
 * difference between an owner's own read and a stranger's is the VALUES of `visibility`/
 * `isOwner`, never the shape.
 */
export const ProfileCvSchema = PublicProfileCvSchema;
export type ProfileCv = PublicProfileCv;

export const SKILL_LEVELS = ["beginner", "intermediate", "advanced", "expert"] as const;
export type SkillLevel = (typeof SKILL_LEVELS)[number];

/** One row of `profiles.portfolio.set` — an empty array clears the whole portfolio server-side. */
export interface PortfolioEntry {
  title?: string;
  role?: string;
  description?: string;
  technologies?: string[];
  outcome?: string;
  linkUrl?: string;
}

export interface ProfilePatch {
  displayName?: string;
  headline?: string;
  bioShort?: string;
  bioExtended?: string;
  avatarUrl?: string;
  timezone?: string;
  availability?: string;
  collaborationStyle?: string;
  languages?: string[];
  visibility?: string;
  publicSlug?: string;
}

export interface ProfileSearchQuery {
  skills?: string[];
  availability?: string;
  timezone?: string;
  offset?: number;
  limit?: number;
}

export const ProfileSearchPageSchema = z.object({
  pagination: z.object({ offset: z.number(), limit: z.number(), total: z.number() }),
  data: z.array(ProfileCvSchema),
});
export type ProfileSearchPage = z.infer<typeof ProfileSearchPageSchema>;

export const SessionSchema = z.object({
  userId: z.string(),
  workspaceId: z.string(),
  createdAt: z.string(),
});
export type Session = z.infer<typeof SessionSchema>;

// ── Automation ───────────────────────────────────────────────────────────────
// Flows, the runs they produce, the schedule that fires them, the agents that execute
// them and the activity stream that records it. Five surfaces over one idea: work that
// happens without somebody clicking.

/** A model or persona this workspace can hand work to. */
export const AgentSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  /** ollama, openai, anthropic … — where the weights live, not what they are called. */
  provider: z.string(),
  category: z.string().nullable(),
  /** Runs on hardware the workspace controls. */
  local: z.boolean(),
  /** The server says this workspace may actually invoke it; the rest are visible but inert. */
  usable: z.boolean(),
  avatarUrl: z.string().nullable(),
});
export type Agent = z.infer<typeof AgentSchema>;

export const FLOW_STATUSES = ["draft", "active", "paused", "archived"] as const;
export type FlowStatus = (typeof FLOW_STATUSES)[number];

export const FlowSchema = z.object({
  id: z.string(),
  /** The identity every other surface uses — the executor, the schedule, the run record. */
  slug: z.string(),
  name: z.string(),
  status: z.enum(FLOW_STATUSES),
  kind: z.string(),
  versionCount: z.number(),
  nodeCount: z.number(),
  documentId: z.string().nullable(),
  updatedAt: z.string(),
  abilities: AbilitiesSchema,
  /** R4 — 'workspace' | 'collection' | 'document' | 'project'. Defaulted so a pre-R4 API still parses. */
  entityLinkKind: z.string().default("workspace"),
  /** R4 — the linked entity's id; null for the workspace sentinel. */
  entityId: z.string().nullable().default(null),
  /** R4 — category tags. */
  categories: z.array(z.string()).default([]),
});
export type Flow = z.infer<typeof FlowSchema>;

/**
 * The list plus the workspace's active-flow policy. The limit is server truth and worth
 * showing: it exists because nothing surfaced the count and it drifted to seven.
 */
export const FlowListSchema = z.object({
  flows: z.array(FlowSchema),
  activeCount: z.number(),
  activeLimit: z.number(),
});
export type FlowList = z.infer<typeof FlowListSchema>;

export const FlowNodeSchema = z.object({
  id: z.string(),
  name: z.string(),
  model: z.string().nullable(),
  effort: z.string().nullable(),
  purpose: z.string().nullable(),
  requireApproval: z.boolean(),
  /** Size of the node's instructions, in tokens, as the server measured them. */
  tokens: z.number(),
});
export type FlowNode = z.infer<typeof FlowNodeSchema>;

export const FlowDetailSchema = z.object({
  slug: z.string(),
  name: z.string(),
  description: z.string(),
  status: z.enum(FLOW_STATUSES),
  kind: z.string(),
  /** The version these nodes came from. Editing a flow makes a new one; a run records which. */
  version: z.number(),
  versionCount: z.number(),
  nodes: z.array(FlowNodeSchema),
  /**
   * The whole definition, as the server stores it: a STRING of JSON, not an object (W3 §C).
   * The adapter used to drop it, which made the studio impossible — a node list is a summary,
   * and an editor that saves a summary deletes everything the summary left out.
   *
   * It stays a string here, and the parsed value stays `unknown` at the zod boundary on
   * purpose: the definition's defining invariant is that UNKNOWN FIELDS ROUND-TRIP VERBATIM
   * (§F-10), and a zod mirror of a schema like that is a field-drop bug factory. The feature
   * casts it to `FlowDefinition` from `@docit/flow-editor`, which is where the shape is
   * authoritative.
   */
  definitionJson: z.string().default(""),
  /** The documentation attached to this flow, if any (`ai-flows.attach-doc`). */
  documentId: z.string().nullable().default(null),
});
export type FlowDetail = z.infer<typeof FlowDetailSchema>;

/**
 * What `ai-flows.save` answers. The server validates on save and REFUSES a definition with an
 * error, so `errors[]` is the studio's most important field: a save that returns `ok: false`
 * is the only place the author learns about the rules the client cannot check (the whole
 * `output` block, unknown models, the `work`/`human` kind rules, subflow slug existence).
 *
 * `unchanged: true` means the definition was byte-identical to the head and no version was
 * minted — worth saying out loud rather than showing a version number that did not move.
 */
export const FlowSaveResultSchema = z.object({
  ok: z.boolean(),
  error: z.string().nullable().default(null),
  version: z.number().nullable().default(null),
  versionId: z.string().nullable().default(null),
  unchanged: z.boolean().default(false),
  warnings: z.array(z.string()).default([]),
  errors: z.array(z.string()).default([]),
});
export type FlowSaveResult = z.infer<typeof FlowSaveResultSchema>;

/** `ai-flows.attach-doc`. `created: true` means the server MATERIALIZED a living doc. */
export const FlowDocAttachmentSchema = z.object({
  documentId: z.string(),
  created: z.boolean(),
  title: z.string().nullable().default(null),
  urlId: z.string().nullable().default(null),
});
export type FlowDocAttachment = z.infer<typeof FlowDocAttachmentSchema>;

/**
 * One past execution of ONE node — the evidence panel behind the Node tab (§A.3).
 *
 * `matchedBy` is load-bearing and must reach the surface: the server widens its search when a
 * flow+node pair has no history, so rows can come from the same node id in a DIFFERENT flow.
 * Printing the numbers without that caveat is how a node gets blamed for another's cost.
 */
export const FlowNodeRunSchema = z.object({
  runId: z.string(),
  runName: z.string().default(""),
  runStatus: z.string().default(""),
  stepStatus: z.string().default(""),
  startedAt: z.string().nullable().default(null),
  endedAt: z.string().nullable().default(null),
  durationMs: z.number().nullable().default(null),
  error: z.string().nullable().default(null),
  actions: z.number().default(0),
  tokens: z
    .object({
      input: z.number().default(0),
      cacheWrite: z.number().default(0),
      cacheRead: z.number().default(0),
      output: z.number().default(0),
      total: z.number().default(0),
    })
    .default({ input: 0, cacheWrite: 0, cacheRead: 0, output: 0, total: 0 }),
  contentTokens: z.number().default(0),
  amplification: z.number().nullable().default(null),
  cacheReadShare: z.number().nullable().default(null),
});
export type FlowNodeRun = z.infer<typeof FlowNodeRunSchema>;

export const FlowNodeHistorySchema = z.object({
  nodeId: z.string(),
  slug: z.string().nullable().default(null),
  /** `flow` | `flow+nodeId` | `nodeId` | `none` — how wide the server had to search. */
  matchedBy: z.string().default("none"),
  matchNote: z.string().default(""),
  runs: z.array(FlowNodeRunSchema).default([]),
});
export type FlowNodeHistory = z.infer<typeof FlowNodeHistorySchema>;

/** The flow record plus its parsed definition — what the studio loads in one call. */
export interface FlowDefinitionResult {
  flow: FlowDetail;
  /** Cast to `FlowDefinition` from `@docit/flow-editor` at the point of use. */
  definition: unknown;
}

export const FlowVersionSchema = z.object({
  version: z.number(),
  createdAt: z.string(),
  createdBy: z.string(),
  note: z.string().nullable(),
  isHead: z.boolean(),
});
export type FlowVersion = z.infer<typeof FlowVersionSchema>;

/** What starting a flow tells you. There is no synchronous result — only where it landed. */
export const FlowRunAckSchema = z.object({
  /** server | ide — which host picked the work up. */
  host: z.string(),
  runId: z.string().nullable(),
  dispatchId: z.string().nullable(),
  target: z.string().nullable(),
  note: z.string(),
});
export type FlowRunAck = z.infer<typeof FlowRunAckSchema>;

export const RunStepsSchema = z.object({
  total: z.number(),
  completed: z.number(),
  failed: z.number(),
});

/** One declared input that did not resolve — named, never merely counted (W2 §A.4). */
export const RunDegradedSourceSchema = z.object({
  nodeId: z.string(),
  sourceId: z.string(),
  kind: z.string(),
  reason: z.string(),
});
export type RunDegradedSource = z.infer<typeof RunDegradedSourceSchema>;

export const RunSchema = z.object({
  id: z.string(),
  aiFlowSlug: z.string().nullable().optional(),
  name: z.string(),
  kind: z.string(),
  /** requested | running | observing | paused | completed | completed-degraded | failed | cancelled */
  status: z.string(),
  createdAt: z.string(),
  startedAt: z.string().nullable(),
  finishedAt: z.string().nullable(),
  durationMs: z.number().nullable(),
  currentNodeId: z.string().nullable(),
  result: z.string().nullable(),
  error: z.string().nullable(),
  steps: RunStepsSchema,
  /** The node whose approval the executor is blocked on — the run is stopped until answered. */
  awaitingApprovalNodeId: z.string().nullable(),
  /** How many declared inputs were missing when it answered. */
  degradedSources: z.number(),
  /** The named detail behind `degradedSources` — W2's viewer renders these, never just the count. */
  degraded: z.array(RunDegradedSourceSchema).default([]),
  /** Set when this run was spawned by another (an amend or a subflow) — drives the back-stack. */
  causalRunId: z.string().nullable().default(null),
  /** Straight from the server's `can`: which control verbs have somewhere real to land. */
  abilities: AbilitiesSchema,
});
export type Run = z.infer<typeof RunSchema>;

export const RunStepSchema = z.object({
  index: z.number(),
  nodeId: z.string(),
  name: z.string(),
  status: z.string(),
  startedAt: z.string().nullable(),
  endedAt: z.string().nullable(),
  durationMs: z.number().nullable(),
  output: z.string(),
  /** True when the server truncated it, or when this adapter did — either way it is not all. */
  outputTruncated: z.boolean(),
  error: z.string().nullable(),
  model: z.string().nullable(),
  tokens: z.number().nullable(),
  /**
   * The raw merged meta the server sends (executor-reported wins over the `ai_actions` rollup),
   * kept whole for the run viewer's node card — `prompt`/`promptChars` live inside it. `null`
   * for an IDE-executed step the rollup has not reached yet.
   */
  meta: z.record(z.string(), z.unknown()).nullable().default(null),
  /** Per-node log lines, rendered under a step's output in the run viewer's Steps tab. */
  log: z.array(z.object({ at: z.string(), message: z.string() })).default([]),
});
export type RunStep = z.infer<typeof RunStepSchema>;

export const RunLogLineSchema = z.object({
  /** The line's position in an append-only log — its only stable identity. */
  id: z.string(),
  at: z.string(),
  nodeId: z.string().nullable(),
  level: z.string(),
  message: z.string(),
});
export type RunLogLine = z.infer<typeof RunLogLineSchema>;

export const RunStatsSchema = z.object({
  windowDays: z.number(),
  totalRuns: z.number(),
  byStatus: z.record(z.string(), z.number()),
  /** W2 — every RunKindWire value, seeded to 0 by the server. */
  byKind: z.record(z.string(), z.number()).default({}),
  byName: z.array(
    z.object({
      name: z.string(),
      count: z.number(),
      successRate: z.number(),
      avgDurationMs: z.number().nullable(),
      tokens: z.number(),
    }),
  ),
  successRate: z.number(),
  terminalRuns: z.number(),
  avgDurationMs: z.number().nullable(),
  /** W2 — the longest sampled run; the KPI band does not show it today but it rides along. */
  maxDurationMs: z.number().nullable().default(null),
  /** W2 — the "Avg duration" KPI's sub-label ("{{count}} sampled"). */
  sampledRuns: z.number().default(0),
  /** W2 — kept as separate categories: "Total tokens" needs `{{in}} in · {{out}} out`. */
  tokens: z.object({
    input: z.number(),
    output: z.number(),
    total: z.number(),
  }),
  daily: z.array(
    z.object({
      date: z.string(),
      total: z.number(),
      completed: z.number(),
      failed: z.number(),
    }),
  ),
});
export type RunStats = z.infer<typeof RunStatsSchema>;

export const ScheduledTaskSchema = z.object({
  id: z.string(),
  title: z.string(),
  /** flow | ide-flow | digest | pulse-advisor — what fires when this row comes due. */
  taskType: z.string(),
  flowId: z.string().nullable(),
  /** at | every | on */
  triggerKind: z.string(),
  recurrence: z.string().nullable(),
  /** For an `on` trigger: the event that fires it (BRIEF-CGNI S2-6). */
  onEvent: z.string().nullable().default(null),
  /** active | paused */
  paused: z.boolean(),
  nextFireAt: z.string().nullable(),
  lastFiredAt: z.string().nullable(),
  abilities: AbilitiesSchema,
});
export type ScheduledTask = z.infer<typeof ScheduledTaskSchema>;

/** Work queued for an editor window to claim — the half of automation that needs a human's machine. */
export const DispatchSchema = z.object({
  id: z.string(),
  kind: z.string(),
  agent: z.string(),
  status: z.string(),
  target: z.string().nullable(),
  claimedBy: z.string().nullable(),
  runId: z.string().nullable(),
  createdAt: z.string(),
  claimedAt: z.string().nullable(),
  expiresAt: z.string().nullable(),
  completedAt: z.string().nullable(),
  result: z.string().nullable(),
  error: z.string().nullable(),
  /** Somebody else's dispatch: you see that a window is busy, never what it was asked. */
  redacted: z.boolean(),

  // ── W4: the nine fields `ai-dispatch.list` already sends and W2 did not map ──────────────
  /** D9 — a web-created dispatch has none, so it appears in no window's OUTBOUND history. */
  originSessionKey: z.string().nullable().default(null),
  conversationId: z.string().nullable().default(null),
  tokenBudget: z.number().nullable().default(null),
  /**
   * The model the dispatch asked the window for (`--model`) and its effort. Null = the window's
   * own default — which is what the header's model chip then says, rather than guessing one.
   */
  model: z.string().nullable().optional(),
  effort: z.string().nullable().optional(),
  /**
   * A raw JSON **string**, kept as one on purpose (§E-12). It is not always JSON, and
   * `promptFromPayload()` is the single defensive reader — an unreadable payload must never
   * blank a row.
   */
  payload: z.string().nullable().default(null),
  isOwn: z.boolean().default(true),
  /**
   * The server's LOWER-CASE, person-facing projection, kept VERBATIM (§E-6). It is not
   * `status` spelt differently: `awaiting-approval`, `unreachable-window` and `declined` have
   * no equivalent in the lifecycle enum at all, which is the whole reason it exists.
   */
  statusDetail: z.string().nullable().default(null),
  /** Counted only for coordinate-targeted rows at the same (machineId, workspacePath). */
  queuedBehind: z.number().default(0),
  ownerUserName: z.string().nullable().default(null),
  /**
   * The run a dispatched flow parked on. `awaitingApprovalNodeId` being non-null is the ONLY
   * thing that raises the approval card in Remote Sessions — and the only approval kind slate
   * can answer without the SignalR hub (§E-3).
   */
  run: z
    .object({
      status: z.string(),
      currentNodeId: z.string().nullable().default(null),
      awaitingApprovalNodeId: z.string().nullable().default(null),
    })
    .nullable()
    .default(null),
});
export type Dispatch = z.infer<typeof DispatchSchema>;

export const ActivityEventSchema = z.object({
  id: z.string(),
  /** A technical identifier ("documents.publish"), deliberately not translated. */
  name: z.string(),
  actor: ActorSchema.nullable(),
  createdAt: z.string(),
  documentId: z.string().nullable(),
  collectionId: z.string().nullable(),
});
export type ActivityEvent = z.infer<typeof ActivityEventSchema>;

export const ActivityPageSchema = z.object({
  events: z.array(ActivityEventSchema),
  offset: z.number(),
  limit: z.number(),
  total: z.number(),
});
export type ActivityPage = z.infer<typeof ActivityPageSchema>;

export const DocumentListSchema = z.array(DocumentSchema);
export const CollectionListSchema = z.array(CollectionSchema);
export const RevisionListSchema = z.array(RevisionSchema);
export const MemberListSchema = z.array(MemberSchema);
export const GroupListSchema = z.array(GroupSchema);
export const ApiKeyListSchema = z.array(ApiKeySchema);
export const ShareListSchema = z.array(ShareSchema);
export const WorkspaceListSchema = z.array(WorkspaceSchema);
export const AgentListSchema = z.array(AgentSchema);
export const FlowVersionListSchema = z.array(FlowVersionSchema);
export const RunListSchema = z.array(RunSchema);
export const RunStepListSchema = z.array(RunStepSchema);
export const RunLogListSchema = z.array(RunLogLineSchema);
export const ScheduledTaskListSchema = z.array(ScheduledTaskSchema);
export const DispatchListSchema = z.array(DispatchSchema);

// ── Document Sculptor ────────────────────────────────────────────────────────
// The platform's AI document engine (`/api/v2/documents/{id}/sculpt*`). One endpoint, two
// lanes: `act` applies through the server's own safe write path (collab bridge or canonical
// update — the server decides, never the client), `propose` stages a reviewable proposal.
// These routes answer bare objects, not the `{ data, policies }` envelope.

export const SCULPT_OPS = [
  "compose",
  "outline",
  "writeSection",
  "insertSection",
  "moveSection",
  "mergeSections",
  "splitSection",
  "retitle",
  "setSummary",
  "setIcon",
  "widgetize",
  "restructure",
] as const;
export type SculptOpKind = (typeof SCULPT_OPS)[number];

/** B6: errors and outcomes name the thinker — "AI is broken" must never be all a user learns. */
export const SculptThinkerSchema = z.object({
  thinkerId: z.string().default(""),
  thinkerName: z.string().default(""),
  model: z.string().default(""),
});
export type SculptThinker = z.infer<typeof SculptThinkerSchema>;

export const SculptProposalSchema = z.object({
  markdown: z.string().default(""),
  summary: z.string().default(""),
});
export type SculptProposal = z.infer<typeof SculptProposalSchema>;

/**
 * What one sculpt (or a resolve/revert of one) came to. Defaulted generously because the
 * server's shapes vary by path — a drifted revert answers only `{ status, opId }`.
 */
export const SculptOutcomeSchema = z.object({
  opId: z.string().nullable().default(null),
  lane: z.string().default("propose"),
  status: z.string(),
  appliedSteps: z.number().default(0),
  totalSteps: z.number().default(0),
  checkpointRevisionId: z.string().nullable().default(null),
  proposal: SculptProposalSchema.nullable().default(null),
  thinker: SculptThinkerSchema.default({ thinkerId: "", thinkerName: "", model: "" }),
  tokensUsed: z.number().default(0),
});
export type SculptOutcome = z.infer<typeof SculptOutcomeSchema>;

/**
 * The newest sculpt row for a document. `revertible` answers "may the revert control show?";
 * `revertMode` answers whether revert restores directly or — because the document has drifted
 * since the apply — opens a proposal instead. Drift never makes revert unavailable.
 */
export const SculptLatestSchema = z.object({
  opId: z.string(),
  /** A wire op, or `revertRestore` on a row minted by a drifted revert. */
  op: z.string(),
  lane: z.string(),
  status: z.string(),
  proposal: SculptProposalSchema.nullable().default(null),
  revertible: z.boolean().default(false),
  revertMode: z.enum(["direct", "propose"]).nullable().default(null),
  createdAt: z.string(),
  thinker: SculptThinkerSchema.default({ thinkerId: "", thinkerName: "", model: "" }),
});
export type SculptLatest = z.infer<typeof SculptLatestSchema>;

/** One SSE frame of a streaming sculpt. `message` rides on errors, `text` everywhere else. */
export interface SculptStreamEvent {
  type: "plan" | "step" | "delta" | "applied" | "proposal" | "degraded" | "error" | (string & {});
  stepIndex?: number | null;
  totalSteps?: number | null;
  appliedSteps?: number | null;
  anchorPartId?: string | null;
  text?: string | null;
  message?: string | null;
  reason?: string | null;
  opId?: string | null;
  checkpointRevisionId?: string | null;
}

// ── The AI ledger ────────────────────────────────────────────────────────────
// The platform's central AI surface (`/api/ai.usage`, `/api/ai.calls`, `/api/queues.digest`):
// every model call the workspace made, across four ledgers kept at four different grains.
// No key joins them, so each lane is reported under its own name with its grain stated —
// the adapter normalises shapes, never merges lanes. All of it is read-only.

/** One model's roll-up in the pulse lane (per-model-call receipts). */
export interface AiPulseModelRow {
  model: string;
  /** Every thinker id that reached this model — the first link of each assignment chain. */
  thinkerIds: string[];
  calls: number;
  failures: number;
  /** Provider-measured tokens. Kept apart from estimates; summing them republishes guesses. */
  measuredTokens: number;
  /** chars/4 guesses, priced but flagged — never folded into `measuredTokens`. */
  estimatedTokens: number;
  durationMs: number;
  costMicros: number;
  /** False = the price came from the fallback rate, not a known vendor price. */
  rateKnown: boolean;
}

/** One model's roll-up in the actions lane (IDE/flow commands, four token categories). */
export interface AiActionsModelRow {
  model: string;
  thinkerIds: string[];
  calls: number;
  inputTokens: number;
  cacheWriteTokens: number;
  cacheReadTokens: number;
  outputTokens: number;
  costMicros: number;
  rateKnown: boolean;
}

export interface AiStageRow {
  stage: string;
  calls: number;
  failures: number;
  tokens: number;
  models: string[];
}

/** A statistical engine — AI with no LLM in it, priced at a stated placeholder rate. */
export interface AiEngineRow {
  engine: string;
  /** The server's own description of what this engine is. */
  kind: string;
  runs: number;
  failed: number;
  durationMs: number;
  costMicros: number;
}

export interface AiFlowSpend {
  flow: string;
  calls: number;
  tokens: number;
  costMicros: number;
}

export interface AiDailyPoint {
  day: string;
  calls: number;
  tokens: number;
  costMicros: number;
}

/** The team-wide roll-up `/api/ai.usage` answers, flattened into Slate's vocabulary. */
export interface AiUsage {
  windowDays: number;
  totals: { calls: number; llmTokens: number; costMicros: number; costUsd: number };
  pulse: {
    /** The server's own statement of what one row means in this lane. */
    grain: string;
    calls: number;
    measuredTokens: number;
    estimatedTokens: number;
    costMicros: number;
    byModel: AiPulseModelRow[];
    byStage: AiStageRow[];
  };
  actions: {
    grain: string;
    calls: number;
    inputTokens: number;
    cacheWriteTokens: number;
    cacheReadTokens: number;
    outputTokens: number;
    costMicros: number;
    byModel: AiActionsModelRow[];
  };
  billing: {
    grain: string;
    tokensIn: number;
    tokensOut: number;
    bySource: Array<{ source: string; tokens: number }>;
  };
  engines: { grain: string; runs: number; costMicros: number; engines: AiEngineRow[] };
  byFlow: AiFlowSpend[];
  daily: AiDailyPoint[];
  /** The server's caveats about how these figures were made. Rendered, never dropped. */
  reading: string[];
}

/** One row of the unified call feed — a model call, an IDE command, or an engine run. */
export interface AiCall {
  at: string;
  /** pulse | actions | engines */
  lane: string;
  /** What the call was for — a stage name, a command summary, an engine run description. */
  what: string;
  thinkerId: string | null;
  model: string | null;
  sessionId: string | null;
  flow: string | null;
  target: string | null;
  tokensIn: number | null;
  tokensOut: number | null;
  tokensTotal: number | null;
  /** False = `tokensTotal` is a chars/4 guess, not a provider measurement. */
  tokensMeasured: boolean;
  durationMs: number;
  costMicros: number;
  /** "ok", or the failure the server recorded — a refused call is recorded, not hidden. */
  outcome: string;
}

export interface AiCallsPage {
  windowDays: number;
  calls: AiCall[];
  reading: string[];
}

/** The article-digestion lane in one view: DB backlog, broker depth, measured drain. */
export interface QueueDigest {
  /** "queue" = messages ride the broker; "inline" = digestion runs in-process. */
  transport: "queue" | "inline";
  payloadEncryption: boolean;
  backlog: { pendingArticles: number; note: string };
  queue: {
    name: string;
    messages: number;
    consumers: number;
    band: { min: number; max: number; scaleUpAt: number };
  } | null;
  /** Messages that failed twice — kept inspectable, never silently dropped. */
  deadLetters: number;
  drain: { lastHour: number; lastDay: number };
}

/** Ability keys the shell itself consults, named once so typos surface in review. */
export const ABILITY = {
  workspaceUpdate: "update",
  workspaceManageMembers: "manageMembers",
  workspaceCreateDocument: "createDocument",
  workspaceCreateCollection: "createCollection",
  workspaceManageApiKeys: "manageApiKeys",
  workspaceManageIntegrations: "manageIntegrations",
  workspaceViewAudit: "viewAudit",
  collectionUpdate: "update",
  collectionDelete: "delete",
  collectionCreateDocument: "createDocument",
  documentUpdate: "update",
  documentDelete: "delete",
  documentShare: "share",
  documentArchive: "archive",
  documentRestore: "restore",
  /**
   * W8 — the document AI layer gates on the HOST DOCUMENT, because not one conversation
   * endpoint projects a `policies` block or a `can` object (W8 §0.3). `comment` is a real key
   * the platform's `BuildAbilities()` already emits; `read` is the plain read gate. Neither is
   * ever derived from a conversation payload.
   */
  documentComment: "comment",
  documentRead: "read",
  /** Flow control. The platform projects no per-flow policy, so these ride the workspace's. */
  flowRun: "run",
  flowUpdate: "update",
  flowDelete: "delete",
  /** Run control, straight from the run's own `can` — the server says where a verb can land. */
  runPause: "pause",
  runResume: "resume",
  runCancel: "cancel",
  runRetry: "retry",
  runApprove: "approve",
  /** W2 — also straight from `can`. Never invented for stop/breakpoints/publish/directives:
   * the server projects no policy for those, so they ride `workspaceUpdate` instead. */
  runStep: "step",
  runJump: "jump",
  taskManage: "manage",
  /**
   * W5 — a project's abilities never ride in `projects.list`'s response (`ApiHelpers.BuildPolicies`
   * is never called for it): they come from a separate `policies.list` batch-resolve, cached
   * per id exactly like every other entity's `{id, abilities}` shape (`governanceTypes.ts`).
   */
  projectUpdate: "update",
  projectDelete: "delete",
  projectManagePermissions: "managePermissions",
} as const;

// ── W9: wisdom graph, merge, share grants, notifications ─────────────────────────────────────
// The governed `wisdom.*` family (`WisdomBrowseController`), the merge/revisions surface
// (`MergeController`/`RevisionsController`), share grants (`ShareGrantsController` + the
// `*.viaGrant` actions) and the inbox (`NotificationsController`). See
// documentation/ai-map/features/slate-feature-transfer/waves/W9-wisdom-merge-grants-notifications.md
// §C.3 for the wire contract these mirror.

export const WISDOM_CERTAINTIES = ["observed", "reported", "inferred"] as const;
export type WisdomCertainty = (typeof WISDOM_CERTAINTIES)[number];
export const WISDOM_LAYERS = ["L1", "L2", "L3"] as const;
export type WisdomLayer = (typeof WISDOM_LAYERS)[number];

export interface WisdomFilters {
  certainty?: WisdomCertainty;
  layer?: WisdomLayer;
}

export const WisdomHitSchema = z.object({
  id: z.string(),
  kind: z.string(),
  title: z.string(),
  score: z.number(),
  scoreKind: z.enum(["cosine", "fulltext"]),
  layer: z.string().nullable().default(null),
  certainty: z.string().nullable().default(null),
  sourceKind: z.string().nullable().default(null),
  provenance: z.record(z.string(), z.unknown()).default({}),
  createdAt: z.string().nullable().default(null),
});
export type WisdomHit = z.infer<typeof WisdomHitSchema>;

export const WisdomSearchResultSchema = z.object({
  query: z.string(),
  hits: z.array(WisdomHitSchema),
  withheld: z.number().default(0),
  degraded: z.boolean().default(false),
  skippedReason: z.string().nullable().default(null),
});
export type WisdomSearchResult = z.infer<typeof WisdomSearchResultSchema>;

const WisdomEdgeReturnedSchema = z.object({
  rel: z.string(),
  direction: z.enum(["out", "in"]),
  id: z.string(),
  kind: z.string(),
  title: z.string(),
  layer: z.string().nullable().default(null),
  certainty: z.string().nullable().default(null),
});

const WisdomDegreeSchema = z.object({ rel: z.string(), totalCount: z.number() });

const WisdomEdgeGroupSchema = z.object({
  rel: z.string(),
  totalCount: z.number(),
  returned: z.array(WisdomEdgeReturnedSchema),
  withheld: z.number().default(0),
  truncated: z.boolean().default(false),
  offset: z.number().default(0),
});

export const WisdomNodeSchema = z.object({
  found: z.literal(true),
  id: z.string(),
  kind: z.string(),
  layer: z.string().nullable().default(null),
  certainty: z.string().nullable().default(null),
  provenance: z.record(z.string(), z.unknown()).default({}),
  properties: z.record(z.string(), z.unknown()).default({}),
  degrees: z.array(WisdomDegreeSchema).default([]),
  edges: z.array(WisdomEdgeGroupSchema).default([]),
});
export type WisdomNode = z.infer<typeof WisdomNodeSchema>;

const WisdomNeighborNodeSchema = z.object({
  id: z.string(),
  kind: z.string(),
  title: z.string(),
  layer: z.string().nullable().default(null),
  certainty: z.string().nullable().default(null),
  provenance: z.record(z.string(), z.unknown()).default({}),
  createdAt: z.string().nullable().default(null),
});

const WisdomNeighborEdgeSchema = z.object({ from: z.string(), to: z.string(), rel: z.string() });

export const WisdomNeighborhoodSchema = z.object({
  found: z.literal(true),
  origin: z.object({ id: z.string(), kind: z.string(), title: z.string() }),
  depth: z.number(),
  nodes: z.array(WisdomNeighborNodeSchema),
  edges: z.array(WisdomNeighborEdgeSchema),
  withheld: z.number().default(0),
  nodeCap: z.number().default(50),
  fanOutCap: z.number().default(20),
  matched: z.number().default(0),
  truncated: z.boolean().default(false),
  fanOutTruncated: z.number().default(0),
});
export type WisdomNeighborhood = z.infer<typeof WisdomNeighborhoodSchema>;

export const WisdomPathSchema = z.object({
  found: z.boolean(),
  withheld: z.boolean().default(false),
  length: z.number().nullable().default(null),
  nodes: z.array(z.record(z.string(), z.unknown())).nullable().default(null),
  rels: z.array(z.string()).nullable().default(null),
});
export type WisdomPath = z.infer<typeof WisdomPathSchema>;

export const WisdomProvenanceSchema = z.object({
  found: z.literal(true),
  id: z.string(),
  kind: z.string(),
  layer: z.string().nullable().default(null),
  certainty: z.string().nullable().default(null),
  /** Shape differs by kind (groveTruth/memory/document/semantic) — kept as the raw chain; every
   * chain carries `store` and `hydrateVia` (§B.2), which is all a generic renderer needs. */
  provenance: z.record(z.string(), z.unknown()),
  withheld: z.number().default(0),
});
export type WisdomProvenance = z.infer<typeof WisdomProvenanceSchema>;

const WisdomOverviewCountSchema = z.object({
  kind: z.string(),
  certainty: z.string(),
  total: z.number(),
});
const WisdomTopDegreeSchema = z.object({
  id: z.string(),
  kind: z.string(),
  title: z.string(),
  layer: z.string().nullable().default(null),
  certainty: z.string().nullable().default(null),
  degree: z.number(),
});
export const WisdomOverviewSchema = z.object({
  counts: z.array(WisdomOverviewCountSchema),
  total: z.number(),
  topDegree: z.array(WisdomTopDegreeSchema),
  withheld: z.number().default(0),
  degraded: z.boolean().default(false),
});
export type WisdomOverview = z.infer<typeof WisdomOverviewSchema>;

// ── wisdom.topdown (CWAY): the project narrated top-down, one component per row ──────────────────

const TopDownDocSchema = z.object({
  id: z.string(),
  title: z.string().nullable().default(null),
  kinds: z.array(z.string()).default([]),
  strength: z.number().default(0),
});

const TopDownSemanticSchema = z.object({
  total: z.number().default(0),
  more: z.number().default(0),
  items: z.array(
    z.object({
      id: z.string(),
      title: z.string().nullable().default(null),
      certainty: z.string().nullable().default(null),
    }),
  ),
});

const TopDownTruthSchema = z.object({
  id: z.string(),
  kind: z.string().default(""),
  statement: z.string().nullable().default(null),
  grade: z.number().default(0),
  weight: z.number().default(0),
  state: z.string().default(""),
  claims: z.string().nullable().default(null),
});

/** A MEASURED edge (consensus of ≥2 assumption families) — a third class beside asserted and semantic. */
const TopDownDrivesSchema = z.object({
  component: z.string(),
  lag: z.number().default(0),
  score: z.number().default(0),
  engines: z.array(z.string()).default([]),
  families: z.number().default(0),
  halfLifeHours: z.number().nullable().default(null),
  measured: z.boolean().default(true),
});

export const WisdomTopDownComponentSchema = z.object({
  component: z.string(),
  kind: z.string().default("code"),
  files: z.number().default(0),
  lastCommitAt: z.string().nullable().default(null),
  coverage: z.object({ state: z.string() }),
  tempo: z.object({ days: z.number().default(30), commits: z.number().default(0) }),
  docs: z
    .object({
      total: z.number().default(0),
      withheld: z.number().default(0),
      more: z.number().default(0),
      items: z.array(TopDownDocSchema),
    })
    .nullable()
    .default(null),
  concepts: TopDownSemanticSchema.nullable().default(null),
  decisions: TopDownSemanticSchema.nullable().default(null),
  patterns: TopDownSemanticSchema.nullable().default(null),
  truths: z
    .object({
      total: z.number().default(0),
      more: z.number().default(0),
      items: z.array(TopDownTruthSchema),
    })
    .nullable()
    .default(null),
  drives: z.array(TopDownDrivesSchema).default([]),
  drivenBy: z.array(TopDownDrivesSchema).default([]),
});
export type WisdomTopDownComponent = z.infer<typeof WisdomTopDownComponentSchema>;

export const WisdomTopDownSchema = z.object({
  scope: z.string().default("workspace"),
  key: z.string().nullable().default(null),
  depth: z.number().default(3),
  components: z.array(WisdomTopDownComponentSchema),
  componentsTotal: z.number().default(0),
  componentsMore: z.number().default(0),
  tempoDays: z.number().default(30),
  withheld: z
    .object({
      documents: z.number().default(0),
      semantic: z.number().default(0),
      truths: z.number().default(0),
    })
    .default({ documents: 0, semantic: 0, truths: 0 }),
  /** The latest causality run's own words — absence of edges is always explained here. */
  causality: z
    .object({
      status: z.string().nullable().default(null),
      reason: z.string().nullable().default(null),
      runAt: z.string().nullable().default(null),
      edges: z.number().default(0),
    })
    .default({ status: null, reason: null, runAt: null, edges: 0 }),
  degraded: z.boolean().default(false),
  skippedReason: z.string().nullable().default(null),
});
export type WisdomTopDown = z.infer<typeof WisdomTopDownSchema>;

/** `wisdom.coverage.code`, unwrapped from its `{data}` envelope — drift and stale pairs. */
export const WisdomCoverageSchema = z.object({
  available: z.boolean().default(false),
  reason: z.string().nullable().default(null),
  thresholdDays: z.number().default(30),
  components: z
    .object({
      code: z.number().default(0),
      documented: z.number().default(0),
      undocumented: z.number().default(0),
      unpaired: z.number().default(0),
    })
    .partial()
    .default({}),
  undocumented: z
    .array(z.object({ component: z.string(), files: z.number().default(0) }))
    .default([]),
  drift: z
    .array(
      z.object({
        documentId: z.string(),
        title: z.string().nullable().default(null),
        paths: z
          .array(z.object({ path: z.string(), quoted: z.string().nullable().default(null) }))
          .default([]),
      }),
    )
    .default([]),
  driftTruncated: z.boolean().default(false),
  stale: z
    .array(
      z.object({
        documentId: z.string(),
        title: z.string().nullable().default(null),
        component: z.string(),
        daysBehind: z.number().default(0),
      }),
    )
    .default([]),
  truncated: z.boolean().default(false),
});
export type WisdomCoverage = z.infer<typeof WisdomCoverageSchema>;

export const ExpertSchema = z.object({
  userId: z.string(),
  name: z.string(),
  areas: z.array(z.string()).default([]),
  documentCount: z.number().default(0),
  relevance: z.number().default(0),
});
export type Expert = z.infer<typeof ExpertSchema>;

// ── Merge + revisions ──────────────────────────────────────────────────────────────────────────

export const MERGE_DECISIONS = ["accept", "reject", "combine"] as const;
export type MergeDecision = (typeof MERGE_DECISIONS)[number];

export const MergeSessionSchema = z.object({
  id: z.string(),
  documentId: z.string(),
  baseRevisionId: z.string(),
  contributingRevisionIds: z.array(z.string()),
  status: z.string(),
  openedById: z.string(),
  openedAt: z.string(),
  resultRevisionId: z.string().nullable().default(null),
});
export type MergeSession = z.infer<typeof MergeSessionSchema>;

const MergePartAuthorSchema = z.object({
  kind: z.enum(["ai", "human"]),
  userId: z.string().nullable().default(null),
  aiModel: z.string().nullable().default(null),
});

const MergePartSideSchema = z.object({
  revisionId: z.string(),
  changed: z.boolean(),
  createdAt: z.string(),
  content: z.unknown().nullable().default(null),
  author: MergePartAuthorSchema,
});

export const MergePartDiffSchema = z.object({
  partId: z.string(),
  partType: z.string(),
  baseContent: z.unknown().nullable().default(null),
  conflict: z.boolean().default(false),
  sides: z.array(MergePartSideSchema),
});
export type MergePartDiff = z.infer<typeof MergePartDiffSchema>;

export const MergeCommitResultSchema = z.object({
  resultRevisionId: z.string(),
  accepted: z.number(),
  rejected: z.number(),
  combined: z.number(),
  unresolved: z.number(),
});
export type MergeCommitResult = z.infer<typeof MergeCommitResultSchema>;

const MergeAuditContributorSchema = z.object({
  kind: z.enum(["ai", "human"]),
  userId: z.string().nullable().default(null),
  aiModel: z.string().nullable().default(null),
});

const MergeAuditResolutionSchema = z.object({
  partId: z.string(),
  decision: z.enum(MERGE_DECISIONS),
  chosenFromRevisionId: z.string().nullable().default(null),
  resolvedById: z.string(),
  resolvedAt: z.string(),
  contributors: z.array(MergeAuditContributorSchema).default([]),
});

export const MergeAuditSchema = z.object({
  resolutions: z.array(MergeAuditResolutionSchema),
  unattributed: z.number().default(0),
});
export type MergeAudit = z.infer<typeof MergeAuditSchema>;

export const DivergenceSchema = z.object({
  documentId: z.string(),
  divergent: z.boolean(),
  distinctAuthors: z.number(),
  sinceRevisionId: z.string().nullable().default(null),
});
export type Divergence = z.infer<typeof DivergenceSchema>;

const RevisionPartAuthorSchema = z.object({
  kind: z.enum(["human", "ai"]),
  userId: z.string().nullable().default(null),
  aiModel: z.string().nullable().default(null),
  triggeredByUserId: z.string().nullable().default(null),
});

export const RevisionPartSchema = z.object({
  id: z.string(),
  revisionId: z.string(),
  partId: z.string(),
  partType: z.string(),
  sequence: z.number(),
  contentHash: z.string(),
  data: z.unknown().nullable().default(null),
  introducedInRevisionId: z.string(),
  author: RevisionPartAuthorSchema,
});
export type RevisionPart = z.infer<typeof RevisionPartSchema>;

// ── Share grants ───────────────────────────────────────────────────────────────────────────────

export const GRANT_LINK_TYPES = ["View", "Comment", "Edit"] as const;
export type GrantLinkType = (typeof GRANT_LINK_TYPES)[number];

export const ShareGrantSchema = z.object({
  id: z.string(),
  documentId: z.string(),
  urlId: z.string(),
  url: z.string(),
  linkType: z.enum(GRANT_LINK_TYPES),
  recipientContactId: z.string().nullable().default(null),
  recipientEmail: z.string().nullable().default(null),
  expiresAt: z.string().nullable().default(null),
  hasPassword: z.boolean().default(false),
  revokedAt: z.string().nullable().default(null),
  active: z.boolean(),
  views: z.number().default(0),
  lastAccessedAt: z.string().nullable().default(null),
  createdAt: z.string(),
});
export type ShareGrant = z.infer<typeof ShareGrantSchema>;

export interface ShareGrantDraft {
  documentId: string;
  linkType?: "view" | "comment" | "edit";
  recipientContactId?: string;
  recipientEmail?: string;
  recipientName?: string;
  expiresAt?: string;
  password?: string;
}

export interface ShareGrantPatch {
  linkType?: "view" | "comment" | "edit";
  expiresAt?: string;
  clearExpiry?: boolean;
  password?: string;
  clearPassword?: boolean;
}

/** The document a grant resolves to — authors withheld server-side, so neither is rendered. */
const GrantDocumentSchema = z.object({
  id: z.string(),
  title: z.string(),
  text: z.string().default(""),
  emoji: z.string().nullable().default(null),
  updatedAt: z.string().nullable().default(null),
});

const GrantOkSchema = z.object({
  status: z.literal("ok"),
  grantId: z.string(),
  linkType: z.enum(GRANT_LINK_TYPES),
  permission: z.string(),
  document: GrantDocumentSchema,
});
const GrantPasswordRequiredSchema = z.object({
  status: z.literal("password_required"),
  invalid: z.boolean().default(false),
});
const GrantGoneSchema = z.object({ status: z.literal("gone") });

/** The soft-200 discriminated union `documents.read.viaGrant` answers (§E-8, §C.3 rule 1) —
 * never a throw, and `share-grants.resolve` (which answers a real 401/410) is never called. */
export const GrantReadSchema = z.discriminatedUnion("status", [
  GrantOkSchema,
  GrantPasswordRequiredSchema,
  GrantGoneSchema,
]);
export type GrantRead = z.infer<typeof GrantReadSchema>;

export const GrantCommentSchema = z.object({
  id: z.string(),
  data: z.unknown(),
  author: z.string(),
  isAnonymous: z.boolean().default(false),
  createdAt: z.string(),
});
export type GrantComment = z.infer<typeof GrantCommentSchema>;

// ── Notifications ──────────────────────────────────────────────────────────────────────────────

export type NotificationChannel = "app" | "email" | "chat";

/** E-13: the wire embeds the actor (and `comment.createdBy`) WITH email. This schema has no
 * `email` field — a plain zod object strips unknown keys, so the PII never reaches the cache. */
const NotificationActorSchema = z.object({
  id: z.string(),
  name: z.string(),
  avatarUrl: z.string().nullable().default(null),
});

const NotificationCommentSchema = z.object({
  id: z.string().optional(),
  createdBy: NotificationActorSchema.optional(),
});

const NotificationDocumentSchema = z.object({
  id: z.string().optional(),
  title: z.string().optional(),
});

export const NotificationSchema = z.object({
  id: z.string(),
  viewedAt: z.string().nullable().default(null),
  archivedAt: z.string().nullable().default(null),
  createdAt: z.string(),
  event: z.string(),
  userId: z.string(),
  actorId: z.string().nullable().default(null),
  actor: NotificationActorSchema.nullable().default(null),
  commentId: z.string().nullable().default(null),
  comment: NotificationCommentSchema.nullable().default(null),
  documentId: z.string().nullable().default(null),
  document: NotificationDocumentSchema.nullable().default(null),
  revisionId: z.string().nullable().default(null),
  collectionId: z.string().nullable().default(null),
});
export type Notification = z.infer<typeof NotificationSchema>;

/** `user.notificationSettings` — a JSON object keyed by event id, each value either a legacy
 * boolean or a per-channel object (§A.5.1 N1). Resolved through `resolveChannel()`. */
export const NotificationPreferencesSchema = z.record(
  z.string(),
  z.union([
    z.boolean(),
    z.object({
      app: z.boolean().optional(),
      email: z.boolean().optional(),
      chat: z.boolean().optional(),
    }),
  ]),
);
export type NotificationPreferences = z.infer<typeof NotificationPreferencesSchema>;
